<template>
  <div class="flex flex-col gap-6">
    <header class="flex flex-col gap-4 border-b pb-6 sm:flex-row sm:items-start sm:justify-between">
      <Button as-child variant="outline" size="lg" class="min-h-12 w-full text-base sm:order-last sm:w-fit sm:shrink-0">
        <RouterLink :to="{ name: 'HolidaysPublicUserCompaniesPage' }">
          <Building2 aria-hidden="true" />
          Change company
        </RouterLink>
      </Button>
      <div v-if="!store.isLoadingCompany && !store.companyError && store.company" class="flex min-w-0 flex-1 flex-col gap-2">
        <h1 class="break-words text-3xl font-semibold tracking-tight">
          {{ store.company.name }}
          <span v-if="store.company.startel_account_number" class="text-2xl font-normal text-muted-foreground">
            - Account {{ store.company.startel_account_number }}
          </span>
        </h1>
        <p class="hidden text-sm text-muted-foreground sm:block">
          Manage your Centratel services.
        </p>
      </div>
    </header>
    <p v-if="store.isLoadingCompany" role="status" class="text-sm text-muted-foreground">
      Loading company…
    </p>
    <Alert v-else-if="store.companyError" variant="destructive" role="alert">
      <AlertTitle>Company unavailable</AlertTitle>
      <AlertDescription>{{ store.companyError }}</AlertDescription>
    </Alert>
    <template v-else-if="store.company">
      <div class="grid gap-4 md:grid-cols-2">
        <section class="flex flex-col gap-4 rounded-xl border bg-card p-5">
          <h2 class="flex items-center gap-2 text-lg font-semibold">
            <CalendarDays class="h-5 w-5" />
            Holidays
          </h2>
          <template v-if="store.company.holiday_access_enabled">
            <p class="text-sm text-muted-foreground">
              {{ store.company.unconfirmed_holiday_schedules_count }} upcoming holidays to confirm.
            </p>
            <Button as-child class="w-fit">
              <RouterLink :to="{ name: 'CompanyHolidaysPage', params: { companyUuid: store.company.uuid } }">
                Open holiday schedule
              </RouterLink>
            </Button>
          </template>
          <p v-else class="text-sm text-muted-foreground">
            Holiday scheduling is not enabled for this company.
          </p>
        </section>
        <section class="flex flex-col gap-4 rounded-xl border bg-card p-5">
          <h2 class="flex items-center gap-2 text-lg font-semibold">
            <MessageCircle class="h-5 w-5" />
            Support requests
          </h2>
          <p class="text-sm text-muted-foreground">
            {{ store.company.open_support_requests_count }} open requests. View replies or ask us for help.
          </p>
          <Button as-child class="w-fit">
            <RouterLink :to="{ name: 'CompanySupportRequestsPage', params: { companyUuid: store.company.uuid } }">
              Open support requests
            </RouterLink>
          </Button>
        </section>
      </div>
    </template>
  </div>
</template>

<script setup>
import { watch } from 'vue'
import { useRoute } from 'vue-router'
import { Building2, CalendarDays, MessageCircle } from 'lucide-vue-next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useUserCompanyStore } from '../stores/userCompanyStore.js'

const route = useRoute()
const store = useUserCompanyStore()

watch(() => route.params.companyUuid, companyUuid => store.fetchCompany(companyUuid), { immediate: true })
</script>
